import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { formatPhone, validatePhone, COUNTRY_CONFIGS } from '../utils/formatters';
import PasswordInput from './PasswordInput';

const PAGE_SIZE = 10;

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    x: 'M18 6L6 18 M6 6l12 12',
    chevronDown: 'M6 9l6 6 6-6',
    building: 'M3 21h18M3 7v14m18-14v14M3 7l9-4 9 4M5 21V11m4 10V11m6 10V11m4 10V11',
    phone: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z',
    user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
    star: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'
};

const defaultVendor = {
    vendorCode: '',
    vendorName: '',
    category: '',
    contactPerson: '',
    status: 'Active',
    email: '',
    password: '',
    phone: '',
    countryCode: '',
    taxId: '',
    address: ''
};

export default function Vendors({ user, operatorDetails }) {
    const [vendors, setVendors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(defaultVendor);
    const [editingId, setEditingId] = useState(null);
    const [toast, setToast] = useState(null);
    const [ratingModal, setRatingModal] = useState(null); // { vendor }
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingHover, setRatingHover] = useState(0);
    const [remarksValue, setRemarksValue] = useState('');
    const [ratingSaving, setRatingSaving] = useState(false);
    const [showImportScreen, setShowImportScreen] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/vendors/${user?.operatorId || 'all'}`);
            setVendors(res.data);
        } catch (err) {
            console.error('Fetch vendors error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get('/api/categories');
            setCategories(res.data.filter(c => c.isActive !== false));
        } catch (err) {
            console.error('Fetch categories error:', err);
        }
    };

    const fetchCountries = async () => {
        try {
            const res = await axios.get('/api/countries');
            setCountries(res.data.filter(c => c.isActive !== false));
        } catch (err) {
            console.error('Fetch countries error:', err);
        }
    };

    useEffect(() => {
        fetchVendors();
        fetchCategories();
        fetchCountries();
    }, [user]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return vendors.filter(v =>
            (v.vendorCode || '').toLowerCase().includes(q) ||
            v.vendorName.toLowerCase().includes(q) ||
            (v.contactPerson || '').toLowerCase().includes(q) ||
            (v.email || '').toLowerCase().includes(q) ||
            (v.phone || '').includes(q)
        );
    }, [vendors, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                operatorId: user?.operatorId || user?._id,
                category: form.category || null
            };

            if (form.phone && !validatePhone(form.phone, operatorDetails?.country)) {
                showToast(`Phone number is invalid for ${operatorDetails?.country || 'the selected country'}.`, 'error');
                return;
            }

            if (editingId) {
                await axios.put(`/api/vendors/${editingId}`, payload);
                showToast('Vendor updated successfully');
            } else {
                await axios.post('/api/vendors', payload);
                showToast('Vendor added successfully');
            }
            fetchVendors();
            closeModal();
        } catch (err) {
            console.error('Save vendor error:', err);
            // Show more detailed error from server if possible
            const serverMsg = err.response?.data?.message;
            const fullError = err.response?.data?.error;
            const msg = serverMsg || fullError || err.message || 'Failed to save vendor. Check all required fields.';
            showToast(msg, 'error');
        }
    };

    const handleDelete = async (vendor) => {
        if (!window.confirm(`Delete vendor "${vendor.vendorName}"?`)) return;
        try {
            await axios.delete(`/api/vendors/${vendor._id}`);
            showToast('Vendor deleted successfully');
            fetchVendors();
        } catch (err) {
            showToast('Failed to delete vendor', 'error');
        }
    };

    const openModal = (vendor = null) => {
        if (vendor) {
            setEditingId(vendor._id);
            setForm({
                vendorCode: vendor.vendorCode || '',
                vendorName: vendor.vendorName,
                category: vendor.category?._id || vendor.category || '',
                contactPerson: vendor.contactPerson || '',
                status: vendor.status || 'Active',
                email: vendor.email || '',
                password: vendor.password || '',
                phone: vendor.phone || '',
                countryCode: vendor.countryCode || '',
                taxId: vendor.taxId || '',
                address: vendor.address || ''
            });
        } else {
            setEditingId(null);
            setForm(defaultVendor);
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(defaultVendor);
    };

    const openRatingModal = (vendor) => {
        setRatingModal({ vendor });
        setRatingValue(vendor.rating || 0);
        setRemarksValue(vendor.remarks || '');
        setRatingHover(0);
    };

    const closeRatingModal = () => {
        setRatingModal(null);
        setRatingValue(0);
        setRemarksValue('');
        setRatingHover(0);
    };

    const handleSaveRating = async () => {
        if (!ratingModal) return;
        setRatingSaving(true);
        try {
            await axios.patch(`/api/vendors/${ratingModal.vendor._id}/rating`, {
                rating: ratingValue || null,
                remarks: remarksValue
            });
            showToast('Rating saved successfully');
            setVendors(prev => prev.map(v =>
                v._id === ratingModal.vendor._id
                    ? { ...v, rating: ratingValue || null, remarks: remarksValue }
                    : v
            ));
            closeRatingModal();
        } catch (err) {
            showToast('Failed to save rating', 'error');
        } finally {
            setRatingSaving(false);
        }
    };

    if (showImportScreen) {
        return (
            <VendorImportWizard
                operatorId={user?.operatorId || user?._id}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchVendors(); }}
            />
        );
    }

    return (
        <div className="vendors-page">
            <style>{`
                .vendors-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .header-section { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; }
                .vendors-page .title-wrap h1 { font-size: 20px; font-weight: 700; color: #1e293b; margin-bottom: 4px; margin-top: 0; }
                .vendors-page .title-wrap p { color: #64748b; font-size: 13px; margin: 0; }
                .add-btn { 
                    background: #00B2A9; color: white; border: none; padding: 10px 20px; 
                    border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s; box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
                }
                .add-btn:hover { background: #16539C; transform: translateY(-1px); }

                .content-box { 
                    background: white; border-radius: 16px; border: 1px solid #e2e8f0; 
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); padding: 24px;
                }
                .toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
                .vendors-page .toolbar h2 { font-size: 16px; font-weight: 700; color: #1e293b; }
                
                .search-box { position: relative; width: 320px; }
                .search-box input { 
                    width: 100%; padding: 10px 12px 10px 40px; border: 1px solid #e2e8f0; border-radius: 10px; 
                    font-size: 14px; outline: none; transition: all 0.2s; background: #f8fafc;
                }
                .search-box input:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
                .search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; }

                .table-container { overflow-x: auto; }
                .vendors-table { width: 100%; border-collapse: collapse; }
                .vendors-table th { 
                    padding: 14px 20px; text-align: left; font-size: 12px; font-weight: 600; 
                    color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;
                    border-bottom: 1px solid #e2e8f0; background: #fff;
                }
                .vendors-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .vendors-table tr:hover { background: #f8fafc; }
                
                .vendor-name-cell { display: flex; align-items: center; gap: 12px; }
                .vendor-icon { 
                    width: 36px; height: 36px; background: #f1f5f9; border-radius: 8px;
                    display: flex; align-items: center; justify-content: center; color: #64748b;
                }
                .vendor-text { font-weight: 600; color: #1e293b; }
                
                .category-badge { 
                    padding: 4px 12px; border-radius: 99px; background: #f1f5f9; 
                    color: #475569; font-size: 12px; font-weight: 600; border: 1px solid #e2e8f0;
                }
                .status-badge { 
                    padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; text-transform: uppercase;
                }
                .status-active { background: #dcfce7; color: #166534; }
                .status-inactive { background: #f1f5f9; color: #475569; }
                
                .actions-cell { display: flex; gap: 8px; }
                .act-btn { 
                    border: none; background: none; padding: 6px; cursor: pointer; border-radius: 6px;
                    color: #94a3b8; transition: all 0.2s;
                }
                .act-btn:hover { background: #f1f5f9; color: #00B2A9; }
                .act-btn.delete:hover { background: #fee2e2; color: #ef4444; }
                .act-btn.rate:hover { background: #fef9c3; color: #d97706; }

                .contact-info { display: flex; flex-direction: column; gap: 2px; }
                .contact-info span { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #64748b; }

                /* Modal */
                .modal-overlay { 
                    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); 
                    backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000;
                    animation: fadeIn 0.2s ease-out;
                }
                .modal-content { 
                    background: white; border-radius: 20px; width: 100%; max-width: 580px; 
                    max-height: 90vh; display: flex; flex-direction: column;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); position: relative;
                    animation: slideUp 0.3s ease-out;
                }
                .modal-header { 
                    padding: 20px 24px; border-bottom: 1px solid #f1f5f9; 
                    display: flex; justify-content: space-between; align-items: center;
                }
                .modal-header h2 { font-size: 18px; font-weight: 700; color: #1e293b; }
                .close-btn { border: none; background: none; cursor: pointer; color: #64748b; padding: 4px; border-radius: 50%; display: flex; }
                .close-btn:hover { background: #f1f5f9; }

                .modal-content form { display: flex; flex-direction: column; flex: 1; min-height: 0; }
                .modal-body { padding: 24px; flex: 1; overflow-y: auto; }
                .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .form-group { display: flex; flex-direction: column; gap: 6px; }
                .form-group.full { grid-column: span 2; }
                .form-label { font-size: 13px; font-weight: 600; color: #475569; }
                .form-input { 
                    padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; 
                    background: #fff; outline: none; transition: all 0.2s;
                }
                .form-input:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
                .form-input::placeholder { color: #cbd5e1; }

                .modal-footer { padding: 20px 24px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 12px; }
                .cancel-btn { 
                    padding: 10px 20px; border: 1px solid #e2e8f0; background: white; border-radius: 8px;
                    font-size: 14px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.2s;
                }
                .cancel-btn:hover { background: #f8fafc; border-color: #cbd5e1; }
                .submit-btn { 
                    padding: 10px 24px; border: none; background: #00B2A9; color: white; border-radius: 8px;
                    font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s;
                }
                .submit-btn:hover { background: #16539C; }

                .toast {
                    position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 10px;
                    background: #1e293b; color: white; z-index: 2000; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }

                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

                /* Rating Modal */
                .rating-modal { max-width: 440px; }
                .star-row { display: flex; gap: 10px; justify-content: center; margin: 8px 0 4px; }
                .star-btn {
                    background: none; border: none; cursor: pointer; padding: 0;
                    transition: transform 0.15s;
                }
                .star-btn:hover { transform: scale(1.2); }
                .star-label { text-align: center; font-size: 13px; color: #94a3b8; min-height: 20px; margin-bottom: 4px; }
                .remarks-area {
                    width: 100%; padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px;
                    font-size: 14px; font-family: inherit; resize: vertical; min-height: 80px;
                    outline: none; transition: all 0.2s; box-sizing: border-box;
                }
                .remarks-area:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                /* Inline mini stars in table */
                .mini-stars { display: flex; gap: 2px; align-items: center; }
                .rating-cell { display: flex; flex-direction: column; gap: 2px; }
                .remarks-preview { font-size: 11px; color: #94a3b8; max-width: 120px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header-section">
                <div className="title-wrap">
                    <h1>Vendor Management</h1>
                    <p>Manage your suppliers, service providers, and partners.</p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button className="add-btn" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }} onClick={() => setShowImportScreen(true)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        Import
                    </button>
                    <button className="add-btn" onClick={() => openModal()}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Add Vendor
                    </button>
                </div>
            </div>

            <div className="content-box">
                <div className="toolbar">
                    <h2>Vendor Directory</h2>
                    <div className="search-box">
                        <span className="search-icon"><Icon d={icons.search} size={18} /></span>
                        <input
                            type="text"
                            placeholder="Search vendors..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        />
                    </div>
                </div>

                <div className="table-container">
                    <table className="vendors-table">
                        <thead>
                            <tr>
                                <th>Vendor Name</th>
                                <th>Vendor ID</th>
                                <th>Contact Person</th>
                                <th>Contact Info</th>
                                <th>Status</th>
                                <th>Rating</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading vendors...</td></tr>
                            ) : pageData.length === 0 ? (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>No vendors found.</td></tr>
                            ) : (
                                pageData.map(vendor => (
                                    <tr key={vendor._id}>
                                        <td>
                                            <div className="vendor-name-cell">
                                                <div className="vendor-icon"><Icon d={icons.building} size={18} /></div>
                                                <div className="vendor-text">{vendor.vendorName}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 700, color: '#16539C', background: '#eef2ff', padding: '6px 10px', borderRadius: '8px' }}>
                                                {vendor.vendorCode || '-'}
                                            </span>
                                        </td>
                                        <td style={{ color: '#475569' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Icon d={icons.user} size={14} color="#94a3b8" />
                                                {vendor.contactPerson || '-'}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="contact-info">
                                                {vendor.phone && (
                                                    <span>
                                                        <Icon d={icons.phone} size={12} />
                                                        {formatPhone(vendor.phone, operatorDetails?.country)}
                                                    </span>
                                                )}
                                                {vendor.email && <span>{vendor.email}</span>}
                                                {!vendor.phone && !vendor.email && '-'}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${vendor.status?.toLowerCase()}`}>
                                                {vendor.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="rating-cell">
                                                <div className="mini-stars">
                                                    {[1, 2, 3, 4, 5].map(s => (
                                                        <svg key={s} width="13" height="13" viewBox="0 0 24 24"
                                                            fill={s <= (vendor.rating || 0) ? '#f59e0b' : 'none'}
                                                            stroke={s <= (vendor.rating || 0) ? '#f59e0b' : '#cbd5e1'}
                                                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                        </svg>
                                                    ))}
                                                </div>
                                                {vendor.remarks && (
                                                    <span className="remarks-preview" title={vendor.remarks}>{vendor.remarks}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="actions-cell">
                                                <button className="act-btn rate" title="Rate Vendor" onClick={() => openRatingModal(vendor)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24"
                                                        fill={vendor.rating ? '#f59e0b' : 'none'}
                                                        stroke={vendor.rating ? '#f59e0b' : 'currentColor'}
                                                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                    </svg>
                                                </button>
                                                <button className="act-btn" title="Edit" onClick={() => openModal(vendor)}>
                                                    <Icon d={icons.edit} size={16} />
                                                </button>
                                                <button className="act-btn delete" title="Delete" onClick={() => handleDelete(vendor)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && filtered.length > PAGE_SIZE && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, gap: 8 }}>
                        {/* Simple pagination logic could go here */}
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingId ? 'Edit Vendor' : 'Add New Vendor'}</h2>
                            <button className="close-btn" onClick={closeModal}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="modal-body">
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Vendor ID / Code</label>
                                        <input
                                            className="form-input"
                                            placeholder="e.g. VEN-001"
                                            value={form.vendorCode}
                                            onChange={e => setForm({ ...form, vendorCode: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vendor Name <span style={{ color: '#ef4444' }}>*</span></label>
                                        <input
                                            className="form-input"
                                            required
                                            placeholder="Company Name"
                                            value={form.vendorName}
                                            onChange={e => setForm({ ...form, vendorName: e.target.value })}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Contact Person</label>
                                        <input
                                            className="form-input"
                                            placeholder="John Doe"
                                            value={form.contactPerson}
                                            onChange={e => setForm({ ...form, contactPerson: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-input"
                                            value={form.status}
                                            onChange={e => setForm({ ...form, status: e.target.value })}
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Email</label>
                                        <input
                                            className="form-input"
                                            type="email"
                                            placeholder="vendor@example.com"
                                            value={form.email}
                                            onChange={e => setForm({ ...form, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Password</label>
                                        <PasswordInput
                                            className="form-input"
                                            placeholder="Enter password"
                                            value={form.password}
                                            onChange={e => setForm({ ...form, password: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Phone</label>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <select
                                                className="form-input"
                                                style={{ width: '100px', padding: '10px 4px' }}
                                                value={form.countryCode}
                                                onChange={e => setForm({ ...form, countryCode: e.target.value })}
                                            >
                                                <option value="">Code</option>
                                                {Object.values(COUNTRY_CONFIGS).map(config => (
                                                    <option key={config.phoneCode} value={config.phoneCode}>
                                                        {config.phoneCode}
                                                    </option>
                                                ))}
                                            </select>
                                            <input
                                                className="form-input"
                                                style={{ flex: 1 }}
                                                placeholder="789 456 123"
                                                value={form.phone}
                                                onChange={e => setForm({ ...form, phone: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group full">
                                        <label className="form-label">Tax ID / GST</label>
                                        <input
                                            className="form-input"
                                            placeholder="Tax ID Number"
                                            value={form.taxId}
                                            onChange={e => setForm({ ...form, taxId: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group full">
                                        <label className="form-label">Address</label>
                                        <input
                                            className="form-input"
                                            placeholder="Full business address"
                                            value={form.address}
                                            onChange={e => setForm({ ...form, address: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="cancel-btn" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="submit-btn">{editingId ? 'Save Changes' : 'Add Vendor'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Rating Modal */}
            {ratingModal && (
                <div className="modal-overlay" onClick={closeRatingModal}>
                    <div className="modal-content rating-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Rate Vendor</h2>
                            <button className="close-btn" onClick={closeRatingModal}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <div className="modal-body">
                            <p style={{ color: '#475569', marginBottom: 16, fontWeight: 600, fontSize: 15 }}>
                                {ratingModal.vendor.vendorName}
                            </p>

                            {/* Star selector */}
                            <div className="star-row">
                                {[1, 2, 3, 4, 5].map(star => (
                                    <button
                                        key={star}
                                        className="star-btn"
                                        onMouseEnter={() => setRatingHover(star)}
                                        onMouseLeave={() => setRatingHover(0)}
                                        onClick={() => setRatingValue(star === ratingValue ? 0 : star)}
                                        title={`${star} star${star > 1 ? 's' : ''}`}
                                    >
                                        <svg width="38" height="38" viewBox="0 0 24 24"
                                            fill={(ratingHover || ratingValue) >= star ? '#f59e0b' : 'none'}
                                            stroke={(ratingHover || ratingValue) >= star ? '#f59e0b' : '#d1d5db'}
                                            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                            style={{ transition: 'all 0.15s', filter: (ratingHover || ratingValue) >= star ? 'drop-shadow(0 0 4px rgba(245,158,11,0.5))' : 'none' }}
                                        >
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                        </svg>
                                    </button>
                                ))}
                            </div>
                            <div className="star-label">
                                {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][ratingHover || ratingValue] || 'Click a star to rate'}
                            </div>

                            {/* Remarks (optional) */}
                            <div className="form-group" style={{ marginTop: 20 }}>
                                <label className="form-label">Remarks <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                                <textarea
                                    className="remarks-area"
                                    placeholder="Add notes about this vendor's performance..."
                                    value={remarksValue}
                                    onChange={e => setRemarksValue(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="cancel-btn" onClick={closeRatingModal}>Cancel</button>
                            <button
                                type="button"
                                className="submit-btn"
                                onClick={handleSaveRating}
                                disabled={ratingSaving}
                                style={{ opacity: ratingSaving ? 0.7 : 1 }}
                            >
                                {ratingSaving ? 'Saving...' : 'Save Rating'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function VendorImportWizard({ operatorId, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActive(e.type === 'dragenter' || e.type === 'dragover');
    };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setDragActive(false);
        if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
    };
    const handleFileChange = (e) => { if (e.target.files?.[0]) processFile(e.target.files[0]); };

    const processFile = (f) => {
        setFile(f);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(e.target.result, { type: 'binary' });
                const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
                setParsedData(data.map(row => Object.fromEntries(Object.keys(row).map(k => [k.trim(), row[k]]))));
            } catch { alert('Failed to parse Excel file.'); }
        };
        reader.readAsBinaryString(f);
    };

    const downloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([{ 'Vendor ID / Code': 'VEN-001', 'Vendor Name': 'ACME Logistics', 'Contact Person': 'John Doe', 'Email': 'john@acme.com', 'Password': 'pass123', 'Phone': '501234567', 'Country Code': '+971', 'Tax ID': 'TAX001', 'Address': '123 Main St', 'Status': 'Active' }]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vendors');
        XLSX.writeFile(wb, 'Vendors_Template.xlsx');
    };

    const handleImport = async () => {
        if (!parsedData.length) return;
        setImporting(true);
        try {
            const mapped = parsedData.map(r => ({
                vendorCode: r['Vendor ID / Code'] || r['Vendor Code'] || r['Vendor ID'] || r['vendorCode'] || '',
                vendorName: r['Vendor Name'], contactPerson: r['Contact Person'],
                email: r['Email'], password: r['Password'] || '123456',
                phone: r['Phone'], countryCode: r['Country Code'] || '',
                taxId: r['Tax ID'] || '', address: r['Address'] || '',
                status: r['Status'] || 'Active', operatorId
            }));
            const res = await fetch('/api/vendors/import', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operatorId, vendors: mapped })
            });
            const data = await res.json();
            if (res.ok) setResult({ createdCount: data.successCount, errors: data.errors || [] });
            else alert(data.message || 'Import failed');
        } catch { alert('Import failed.'); }
        finally { setImporting(false); }
    };

    const S = { page: { padding: 30, background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter',sans-serif" } };

    if (result) return (
        <div style={S.page}>
            <div style={{ background: 'white', borderRadius: 16, padding: 40, textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9', maxWidth: 700, margin: '0 auto' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 80, height: 80, borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: 24 }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>Import Completed!</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 500, margin: '0 auto 30px' }}>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: '#1e40af' }}>{result.createdCount || 0}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', marginTop: 4 }}>Vendors Created</div>
                    </div>
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: '#92400e' }}>{result.errors?.length || 0}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#b45309', marginTop: 4 }}>Skipped / Errors</div>
                    </div>
                </div>
                <button onClick={onDone} style={{ padding: '12px 30px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Return to Vendors</button>
            </div>
        </div>
    );

    return (
        <div style={S.page}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 30 }}>
                <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                </button>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', margin: 0 }}>Import Vendors</h1>
                    <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Bulk import vendors via Excel upload.</p>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 30 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: 24 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {['Vendor ID / Code', 'Vendor Name','Contact Person','Email','Password','Phone','Country Code','Tax ID','Address','Status'].map((f,i) => (
                                    <span key={i} style={{ fontSize: 11, fontWeight: 600, color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: 6 }}>{f}</span>
                                ))}
                            </div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: 16, border: '1px dashed #cbd5e1', padding: 20, textAlign: 'center' }}>
                            <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px' }}>Download a pre-filled sample template.</p>
                            <button onClick={downloadTemplate} style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Download Template</button>
                        </div>
                    </div>
                    <div onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
                        onClick={() => document.getElementById('vendor-file-input').click()}
                        style={{ background: dragActive ? '#f0f7ff' : 'white', borderRadius: 16, border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <input id="vendor-file-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }}/>
                        <div style={{ width: 64, height: 64, borderRadius: 16, background: '#eef2ff', color: '#16539C', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        </div>
                        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: 12, color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: 6, border: '1px solid #f1f5f9' }}>Supports .xlsx, .xls formats</span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{file?.name}</span>
                            <span style={{ fontSize: 12, color: '#64748b', background: '#cbd5e1', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>{parsedData.length} Rows</span>
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button onClick={() => { setFile(null); setParsedData([]); }} style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Change File</button>
                            <button onClick={handleImport} disabled={importing} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{importing ? 'Processing...' : `Proceed Import (${parsedData.length})`}</button>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: 500 }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead><tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Vendor Name','Contact Person','Email','Status'].map(h => <th key={h} style={{ padding: '14px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>{h}</th>)}
                            </tr></thead>
                            <tbody>
                                {parsedData.map((d,i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '14px 20px', fontWeight: 700, color: '#1e293b' }}>{d['Vendor Name'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: 13 }}>{d['Contact Person'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: 13 }}>{d['Email'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: 13 }}>{d['Status'] || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
